import { Match } from 'effect';
import { isNil } from 'lodash';
import { Numbers } from 'venn-utils';
import { isPercent } from './scatter-axis-format';
import type { AxisFormat } from './scatter-axis-format';

export const displayToChart = (value: number, format: AxisFormat): number =>
  Match.value(format).pipe(
    Match.tag('Percent', () => value / 100),
    Match.tag('Ratio', () => value),
    Match.exhaustive,
  );

export const chartToDisplay = (value: number, format: AxisFormat): number =>
  Match.value(format).pipe(
    Match.tag('Percent', () => Numbers.correctFloat(value * 100)),
    Match.tag('Ratio', () => value),
    Match.exhaustive,
  );

export const formatDisplayValue = (value: number, format: AxisFormat, precision?: number): string => {
  if (isPercent(format)) {
    const display = Numbers.correctFloat(value * 100);
    return `${display.toFixed(precision ?? Numbers.precisionFor(display))}%`;
  }
  return value.toFixed(precision ?? Numbers.precisionFor(value));
};

// Decimals to display for a value already in display units: the unit's baseline precision, but never
// fewer than the value's own decimals, so user-entered precision longer than the baseline is shown in
// full rather than truncated. correctFloat first to drop binary-float noise before counting decimals.
export const axisDisplayPrecision = (value: number): number => {
  const cleaned = Numbers.correctFloat(value);
  return Math.max(Numbers.precisionFor(cleaned), Numbers.decimalCount(cleaned));
};

// Formats a value already in display units to its baseline precision with thousands separators,
// without any unit suffix. Shared by the suffixed display path and the input field so a filled
// field and its placeholder always render the same number identically.
export const formatDisplayUnitNumber = (value: number, precision?: number): string =>
  Numbers.formatNumber(value, precision ?? axisDisplayPrecision(value));

// Use when `value` is already in display units (e.g. percentage as 5, not 0.05) — unlike
// formatDisplayValue which converts from chart units. Appends % suffix for percent formats.
export const formatDisplayUnitValue = (value: number, format: AxisFormat, precision?: number): string => {
  const formatted = formatDisplayUnitNumber(value, precision);
  return isPercent(format) ? `${formatted}%` : formatted;
};

// Highcharts guarantees tickPositions is sorted ascending, so positions[0] is the lowest tick
// and positions[length-1] is the highest. Overwriting them aligns the axis labels with the
// user-set bounds without changing the number of ticks or their spacing.
export const forceEndTicks = (positions: readonly number[], overrides: { min?: number; max?: number }): number[] => {
  const nextPositions = [...positions];

  if (!isNil(overrides.min) && nextPositions[0] !== overrides.min) {
    nextPositions[0] = overrides.min;
  }

  if (!isNil(overrides.max) && nextPositions[nextPositions.length - 1] !== overrides.max) {
    nextPositions[nextPositions.length - 1] = overrides.max;
  }

  return nextPositions;
};

// Rendered size of an axis label, measured (not estimated) from the chart renderer.
export type LabelBox = { width: number; height: number };

// Rotations the axis labels can take, from least to most aggressive.
const ROTATION_ANGLES = [0, -45] as const;
type ScatterRotation = (typeof ROTATION_ANGLES)[number];

// The steepest rotation the x-axis labels may take — the last and most aggressive entry above. Beyond
// this angle no rotation exists to rescue a crowded pair, so it sets the x end-drop threshold.
const STEEPEST_ROTATION: ScatterRotation = ROTATION_ANGLES[ROTATION_ANGLES.length - 1]!;

// Minimum gap between two height-limited labels, as a multiple of label height: a rotated label's strip
// thickness (x) or a horizontal label stacked on the y-axis. 1 = exactly touching; the small excess
// keeps them readable. (At -45 the resulting tick gap is ~1.5x the height: 1.06 / sin45.)
const HEIGHT_CLEARANCE = 1.06;

// Minimum axis gap (px) at which two height-limited labels clear: the taller height plus readability
// margin. It is a rotated label's strip thickness (its perpendicular extent) and the footprint of a
// horizontal label stacked along the y-axis.
const heightFootprint = (left: LabelBox, right: LabelBox): number =>
  Math.max(left.height, right.height) * HEIGHT_CLEARANCE;

// Horizontal reach of a label toward an adjacent one at 0deg. An `overflow: 'justify'` boundary label
// is shifted fully inside the plot box, so it reaches its WHOLE width toward its neighbour; a centered
// interior label reaches half.
const horizontalReach = (box: LabelBox, isBoundary: boolean): number => (isBoundary ? box.width : box.width / 2);

// Whether two adjacent labels clear each other at `angleDeg`. At 0deg the labels are horizontal, so
// their widths must not overlap: the reaches toward one another sum to no more than the pixel gap.
// Once rotated they become parallel slanted strips, translated along the axis by the gap. Their
// bounding-box projections onto the axis overlap, but the strips themselves only collide when the
// perpendicular distance between them — gap*sin|angle| — drops below the strip thickness (the label
// height). Width is irrelevant there, since equal strips slide past each other, so a wide label clears
// at -45 long before its flat axis projection would suggest.
const pairFitsAt = (
  gapPx: number,
  left: LabelBox,
  right: LabelBox,
  angleDeg: number,
  leftBoundary: boolean,
  rightBoundary: boolean,
): boolean => {
  if (angleDeg === 0) {
    return horizontalReach(left, leftBoundary) + horizontalReach(right, rightBoundary) <= gapPx;
  }
  const sin = Math.abs(Math.sin((angleDeg * Math.PI) / 180));
  return gapPx * sin >= heightFootprint(left, right);
};

// Two horizontal labels stacked along the y-axis clear when the pixel gap between them is at least the
// labels' height footprint. Drives the y crowd-thinning below; the y-axis never rotates, so height is
// the whole story. A non-finite gap (axis not laid out yet) counts as clearing.
const ticksClearByHeight =
  (toPixels: (value: number) => number, measure: (value: number) => LabelBox) =>
  (a: number, b: number): boolean => {
    const gap = Math.abs(toPixels(a) - toPixels(b));
    return !Number.isFinite(gap) || gap >= heightFootprint(measure(a), measure(b));
  };

// Two x-axis ticks are beyond rescue by rotation when even the steepest rotation's slanted strip
// overlaps — i.e. the gap drops below heightFootprint / sin|STEEPEST_ROTATION|. Drives the x end-drop
// below: rotation handles every other kind of crowding, so the only ticks worth removing are end
// neighbours the steepest angle still can't separate. A non-finite gap (axis not laid out yet) counts
// as clearing. Boundary flags are irrelevant once rotated (width drops out), so both pass false.
const ticksClearByRotation =
  (toPixels: (value: number) => number, measure: (value: number) => LabelBox) =>
  (a: number, b: number): boolean => {
    const gap = Math.abs(toPixels(a) - toPixels(b));
    return !Number.isFinite(gap) || pairFitsAt(gap, measure(a), measure(b), STEEPEST_ROTATION, false, false);
  };

// Drop an end tick's immediate neighbour when even the steepest rotation cannot separate them.
// forceEndTicks snaps positions[0]/[last] onto the user min/max, which can land them right next to
// positions[1]/[last-1]; once the gap is too small for even the steepest slanted strip to clear, no
// rotation helps, so the inner neighbour (second / penultimate) is removed while the snapped end tick
// itself is kept. Used by the x-axis, where rotation handles interior crowding, so only the snapped
// ends ever need dropping.
export const dropCrowdedEndTicks = (
  positions: readonly number[],
  toPixels: (value: number) => number,
  measure: (value: number) => LabelBox,
): number[] => {
  const next = [...positions];
  if (next.length < 3) {
    return next;
  }

  const clears = ticksClearByRotation(toPixels, measure);

  if (!clears(next[0]!, next[1]!)) {
    next.splice(1, 1);
  }

  if (next.length >= 3 && !clears(next[next.length - 1]!, next[next.length - 2]!)) {
    next.splice(next.length - 2, 1);
  }

  return next;
};

// Thin a whole axis greedily so no two horizontal labels overlap, keeping both snapped boundary ticks.
// The y-axis labels never rotate, so crowding is resolved by dropping ticks rather than rotating: walk
// the interior low→high, keeping a tick only when it clears the last kept by a label height, then drop
// any kept interior ticks still crowding the forced top boundary. Unlike dropCrowdedEndTicks (ends
// only), this also removes interior ticks, since the y-axis has no rotation fallback. Both boundaries
// are always kept even if they themselves crowd, matching the end-drop behaviour.
export const dropCrowdedTicks = (
  positions: readonly number[],
  toPixels: (value: number) => number,
  measure: (value: number) => LabelBox,
): number[] => {
  if (positions.length < 3) {
    return [...positions];
  }

  const clears = ticksClearByHeight(toPixels, measure);
  const topBoundary = positions[positions.length - 1]!;
  const kept = [positions[0]!];

  for (let i = 1; i < positions.length - 1; i++) {
    if (clears(kept[kept.length - 1]!, positions[i]!)) {
      kept.push(positions[i]!);
    }
  }

  while (kept.length > 1 && !clears(kept[kept.length - 1]!, topBoundary)) {
    kept.pop();
  }
  kept.push(topBoundary);

  return kept;
};

// Smallest rotation in [0, -45] at which every adjacent label pair clears its neighbour. A
// non-finite gap (axis not laid out yet) is treated as fitting. Highcharts' own unsquish bases this
// on tickInterval (meaningless for the snapped end ticks) and on label height rather than width,
// which is why the decision is made here from real pixel gaps and measured label geometry.
export const computeScatterRotation = (
  positions: readonly number[],
  toPixels: (value: number) => number,
  measure: (value: number) => LabelBox,
): ScatterRotation => {
  const last = positions.length - 1;

  const fitsEverywhere = (angleDeg: number): boolean => {
    for (let i = 0; i < last; i++) {
      const a = positions[i]!;
      const b = positions[i + 1]!;
      const gap = Math.abs(toPixels(a) - toPixels(b));
      if (Number.isFinite(gap) && !pairFitsAt(gap, measure(a), measure(b), angleDeg, i === 0, i + 1 === last)) {
        return false;
      }
    }
    return true;
  };

  return ROTATION_ANGLES.find(fitsEverywhere) ?? STEEPEST_ROTATION;
};

// Resolve the x-axis tick set and label rotation together: drop the end neighbours that no rotation
// can separate, then pick the gentlest rotation that clears the survivors.
export const resolveScatterXAxisLabels = (input: {
  positions: readonly number[];
  toPixels: (value: number) => number;
  measure: (value: number) => LabelBox;
}): { positions: number[]; rotation: ScatterRotation } => {
  const positions = dropCrowdedEndTicks(input.positions, input.toPixels, input.measure);
  return { positions, rotation: computeScatterRotation(positions, input.toPixels, input.measure) };
};
